import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, HardHat, Calendar, User, MessageSquare,
  FileText, Image as ImageIcon, FileSpreadsheet, FileArchive,
  File as FileIcon, Download, Briefcase, Info,
} from 'lucide-react';
import {
  trouverChantier, utilisateurClient, typesProjet,
  formatDate, formatDateTime,
} from '../data/mockData';

const docIcon = {
  pdf: FileText,
  image: ImageIcon,
  doc: FileText,
  xls: FileSpreadsheet,
  zip: FileArchive,
};

const docBadge = {
  pdf: 'danger',
  image: 'info',
  doc: 'sky',
  xls: 'ok',
  zip: 'warn',
};

const docLabel = {
  pdf: 'PDF',
  image: 'Image',
  doc: 'DOC',
  xls: 'XLSX',
  zip: 'ZIP',
};

const statutBadge = (statut) => {
  if (statut === 'livre') return 'ok';
  if (statut === 'etude') return 'warn';
  return 'info';
};

const statutText = (statut) => {
  if (statut === 'livre') return 'Livre';
  if (statut === 'etude') return 'En etude';
  return 'En cours';
};

const ProjetClient = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { chantier, client } = useMemo(() => trouverChantier(id), [id]);

  if (!chantier || !client || client.id !== utilisateurClient.clientId) {
    return (
      <div className="page-container">
        <button className="back-link" onClick={() => navigate('/espace-client')}>
          <ArrowLeft size={16} /> Retour a mes projets
        </button>
        <div className="empty-state">
          <HardHat size={28} />
          <p>Projet introuvable ou non autorise.</p>
        </div>
      </div>
    );
  }

  const typeProjet = typesProjet.find((t) => t.id === chantier.typeProjet) || typesProjet[0];
  const documents = chantier.documentsClient || [];

  return (
    <div className="page-container">
      <button className="back-link" onClick={() => navigate('/espace-client')}>
        <ArrowLeft size={16} /> Retour a mes projets
      </button>

      <div className="page-header">
        <div>
          <div className="page-title-row">
            <div className="page-icon-wrapper">
              <HardHat size={22} />
            </div>
            <div>
              <h1 className="page-title">{chantier.nom}</h1>
              <p className="page-subtitle">
                <span className="mono">{chantier.id}</span> - Projet suivi par ITNI
              </p>
            </div>
          </div>
        </div>
        <div className="page-actions">
          <span className={`badge badge-${typeProjet.badge}`}>{typeProjet.label}</span>
          <span className={`badge badge-${statutBadge(chantier.statut)}`}>
            {statutText(chantier.statut)}
          </span>
        </div>
      </div>

      <div className="kpi-grid kpi-grid-4">
        <div className="kpi-card">
          <div className="kpi-icon"><Briefcase size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Type de projet</span>
            <span className="kpi-value" style={{ fontSize: '1rem' }}>{typeProjet.label}</span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon"><Calendar size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Avancement</span>
            <span className="kpi-value">{chantier.avancement}%</span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon"><User size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Responsable ITNI</span>
            <span className="kpi-value" style={{ fontSize: '0.9rem' }}>
              {chantier.responsable || '-'}
            </span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon"><FileText size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Documents partages</span>
            <span className="kpi-value">{documents.length}</span>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.25rem' }}>
        <header className="card-header">
          <div className="card-title">
            <Info size={18} />
            <h2>Synthese du projet</h2>
          </div>
        </header>
        <div className="card-body info-grid">
          <div>
            <span className="info-label">Identifiant projet</span>
            <span className="info-value mono">{chantier.id}</span>
          </div>
          <div>
            <span className="info-label">Type de projet</span>
            <span className="info-value">{typeProjet.label}</span>
          </div>
          <div>
            <span className="info-label"><Calendar size={12} /> Demarrage</span>
            <span className="info-value">{formatDate(chantier.dateDebut)}</span>
          </div>
          <div>
            <span className="info-label"><Calendar size={12} /> Fin prevue</span>
            <span className="info-value">{formatDate(chantier.dateFinPrevue)}</span>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <span className="info-label">Avancement</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
              <div className="progress" style={{ flex: 1, maxWidth: 360 }}>
                <div className="progress-bar" style={{ width: `${chantier.avancement}%` }} />
              </div>
              <span className="mono">{chantier.avancement}%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.25rem' }}>
        <header className="card-header">
          <div className="card-title">
            <MessageSquare size={18} />
            <h2>Informations client</h2>
          </div>
          {chantier.infosClientMaj && (
            <span className="meta-line">
              Mise a jour le {formatDateTime(chantier.infosClientMaj)} par {chantier.infosClientPar}
            </span>
          )}
        </header>
        <div className="card-body">
          {chantier.infosClient ? (
            <div className="infos-client-preview infos-client-preview-large">
              {chantier.infosClient.split('\n').map((line, i) => (
                <p key={i}>{line || '\u00A0'}</p>
              ))}
            </div>
          ) : (
            <div className="empty-state compact">
              <MessageSquare size={22} />
              <p>Aucune information n a encore ete communiquee sur ce projet.</p>
              <p className="meta-line">
                Votre interlocuteur ITNI partagera ici toute information utile au suivi.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.25rem' }}>
        <header className="card-header">
          <div className="card-title">
            <FileText size={18} />
            <h2>Documents mis a votre disposition ({documents.length})</h2>
          </div>
        </header>
        <div className="card-body">
          {documents.length === 0 ? (
            <div className="empty-state compact">
              <FileText size={22} />
              <p>Aucun document partage pour le moment.</p>
              <p className="meta-line">
                Les documents (compte-rendus, plans, DOE, photos...) apparaitront ici des qu ils seront mis a disposition.
              </p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Type</th>
                  <th>Categorie</th>
                  <th>Date de depot</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => {
                  const Icon = docIcon[doc.type] || FileIcon;
                  const badge = docBadge[doc.type] || 'neutral';
                  return (
                    <tr key={doc.id} className="hoverable">
                      <td>
                        <div className="cell-strong">
                          <Icon size={16} className="cell-icon" />
                          <span className="mono" style={{ fontSize: '0.82rem' }}>{doc.nom}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge badge-${badge}`}>{docLabel[doc.type] || 'Fichier'}</span>
                      </td>
                      <td>
                        <span className="chip chip-sky">{doc.categorie}</span>
                      </td>
                      <td>{formatDateTime(doc.dateAjout)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn-primary btn-small">
                          <Download size={14} /> Telecharger
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjetClient;
